import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface RevealConfig {
  image_reveal_type: string;
  image_reveal_duration: number;
  image_reveal_easing: string;
  image_reveal_stagger: number;
  image_reveal_distance: number;
}

// Module-level cache
let cachedConfig: RevealConfig | null = null;
let configPromise: Promise<RevealConfig> | null = null;

export function invalidateRevealCache() {
  cachedConfig = null;
  configPromise = null;
}

async function loadConfig(): Promise<RevealConfig> {
  if (cachedConfig) return cachedConfig;
  if (configPromise) return configPromise;

  configPromise = (async () => {
    const { data } = await supabase
      .from('site_settings')
      .select('image_reveal_type, image_reveal_duration, image_reveal_easing, image_reveal_stagger, image_reveal_distance')
      .limit(1)
      .maybeSingle();
    const config: RevealConfig = {
      image_reveal_type: (data as any)?.image_reveal_type || 'none',
      image_reveal_duration: (data as any)?.image_reveal_duration || 600,
      image_reveal_easing: (data as any)?.image_reveal_easing || 'ease-out',
      image_reveal_stagger: (data as any)?.image_reveal_stagger || 100,
      image_reveal_distance: (data as any)?.image_reveal_distance || 30,
    };
    cachedConfig = config;
    return config;
  })();

  return configPromise;
}

function getInitialStyle(type: string, distance: number): React.CSSProperties {
  switch (type) {
    case 'fade-in':
      return { opacity: 0 };
    case 'slide-up':
      return { opacity: 0, transform: `translateY(${distance}px)` };
    case 'slide-down':
      return { opacity: 0, transform: `translateY(-${distance}px)` };
    case 'slide-left':
      return { opacity: 0, transform: `translateX(${distance}px)` };
    case 'slide-right':
      return { opacity: 0, transform: `translateX(-${distance}px)` };
    case 'scale-up':
      return { opacity: 0, transform: 'scale(0.92)' };
    case 'blur-in':
      return { opacity: 0, filter: 'blur(8px)' };
    case 'clip-reveal':
      return { clipPath: 'inset(0 100% 0 0)' };
    case 'rotate-in':
      return { opacity: 0, transform: 'rotate(-3deg) scale(0.95)' };
    case 'zoom-out':
      return { opacity: 0, transform: 'scale(1.1)' };
    default:
      return {};
  }
}

function getRevealedStyle(type: string): React.CSSProperties {
  switch (type) {
    case 'fade-in':
      return { opacity: 1 };
    case 'slide-up':
    case 'slide-down':
    case 'slide-left':
    case 'slide-right':
      return { opacity: 1, transform: 'translate(0, 0)' };
    case 'scale-up':
      return { opacity: 1, transform: 'scale(1)' };
    case 'blur-in':
      return { opacity: 1, filter: 'blur(0)' };
    case 'clip-reveal':
      return { clipPath: 'inset(0 0 0 0)' };
    case 'rotate-in':
      return { opacity: 1, transform: 'rotate(0) scale(1)' };
    case 'zoom-out':
      return { opacity: 1, transform: 'scale(1)' };
    default:
      return {};
  }
}

function getTransitionProperties(type: string): string {
  switch (type) {
    case 'fade-in': return 'opacity';
    case 'slide-up':
    case 'slide-down':
    case 'slide-left':
    case 'slide-right':
    case 'scale-up':
    case 'rotate-in':
    case 'zoom-out':
      return 'opacity, transform';
    case 'blur-in': return 'opacity, filter';
    case 'clip-reveal': return 'clip-path';
    default: return 'opacity';
  }
}

// Global stagger counter — resets on page change
let staggerCounter = 0;
let lastResetTime = 0;

function getStaggerDelay(stagger: number): number {
  const now = Date.now();
  // Reset counter if more than 2 seconds since last call (new page/section)
  if (now - lastResetTime > 2000) {
    staggerCounter = 0;
  }
  lastResetTime = now;
  const delay = staggerCounter * stagger;
  staggerCounter++;
  return delay;
}

interface ImageRevealProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Override stagger index for explicit control */
  index?: number;
}

export function ImageReveal({ children, className, style, index }: ImageRevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);
  const [config, setConfig] = useState<RevealConfig | null>(null);
  const delayRef = useRef(0);

  useEffect(() => {
    loadConfig().then((c) => {
      setConfig(c);
      if (c.image_reveal_type !== 'none') {
        delayRef.current = index !== undefined
          ? index * c.image_reveal_stagger
          : getStaggerDelay(c.image_reveal_stagger);
      }
    });
  }, [index]);

  useEffect(() => {
    if (!config || config.image_reveal_type === 'none') {
      setRevealed(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setRevealed(true), delayRef.current);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [config]);

  if (!config || config.image_reveal_type === 'none') {
    return <div className={className} style={style}>{children}</div>;
  }

  const type = config.image_reveal_type;
  const initialStyle = getInitialStyle(type, config.image_reveal_distance);
  const revealedStyle = getRevealedStyle(type);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        ...(revealed ? revealedStyle : initialStyle),
        transitionProperty: getTransitionProperties(type),
        transitionDuration: `${config.image_reveal_duration}ms`,
        transitionTimingFunction: config.image_reveal_easing,
        willChange: revealed ? 'auto' : getTransitionProperties(type),
      }}
    >
      {children}
    </div>
  );
}
